import api from '@mint-lib/api';
import { useQuery } from '@mint-lib/query-client';
import { getRouteApi } from '@mint/routing';

import { HISTORICAL_DATA_STATUSES } from '../constants/campaignBuilder.js';
import { CHECK_HISTORICAL_DATA } from '../constants/EndpointConstants.js';
import { QueryKey } from '../constants/queryKeys.js';
import { getBudgetRows } from '../pages/Builder/BuilderHeader/utils.js';
import {
  CheckHistoricalDataPayload,
  CheckHistoricalDataResponse,
} from '../types/predictivePlanning.js';
import { useBuyingPlanInfoSelector } from './useGetBuyingPlanData.js';
import { useGetMediaPlanRows } from './useGetMediaPlanRows.js';
import { usePredictivePlanningMode } from './usePredictivePlanningMode.js';

const checkHistoricalData = async (payload: CheckHistoricalDataPayload) => {
  const response = await api.post(CHECK_HISTORICAL_DATA, payload);
  return response.data;
};

const routeApi = getRouteApi(
  '/_secured/campaign/$campaignId/planning/buying-plan',
);

const useCheckHistoricalData = () => {
  const params = routeApi.useParams();
  const selectedPlan = useBuyingPlanInfoSelector();
  const preparedRows = useGetMediaPlanRows();
  const isPredictivePlanning = usePredictivePlanningMode();

  const planStartDate = selectedPlan?.dateFrom ?? '';
  const planEndDate = selectedPlan?.dateTo ?? '';

  const budgetRows = getBudgetRows({
    rows: preparedRows,
    planStartDate,
    planEndDate,
  });

  const payload = {
    totalBudget: selectedPlan ? Number(selectedPlan.budget) : 0,
    targetKpi: null,
    clientId: selectedPlan ? String(selectedPlan.company) : '',
    journeyId: params.campaignId,
    flightStartDate: planStartDate,
    flightEndDate: planEndDate,
    mediaRows: budgetRows ?? {},
  };

  const { data: historicalDataStatus, isLoading } = useQuery({
    queryKey: [QueryKey.CHECK_HISTORICAL_DATA, payload],
    queryFn: () => checkHistoricalData(payload),
    enabled: !!selectedPlan && !!preparedRows.length && !isPredictivePlanning,
    select: (data: CheckHistoricalDataResponse) => data.status,
    staleTime: Infinity,
    retry: false,
  });

  return {
    status: historicalDataStatus ?? HISTORICAL_DATA_STATUSES.UNAVAILABLE,
    isLoading,
  };
};

export default useCheckHistoricalData;
